import { useMutation } from '@apollo/client/react';
import { ADD_AND_SEND_PHONE_NUMBER_VERIFICATION_CODE_MUTATION } from '../../integrations/allkort/graphql/gql/PhoneMutations';
import { ResendPhoneVerificationCodeResponse } from '../../integrations/allkort/graphql/interface/Phones';

/**
 * useResendPhoneVerificationCode
 *
 * Resends a verification code via SMS to a previously added (but unverified) phone number.
 *
 * @example
 * ```tsx
 * const { resendCode, loading } = useResendPhoneVerificationCode();
 *
 * const handleResend = async () => {
 *   const result = await resendCode({ phoneNumberId: 939755 });
 *   if (result) console.log('Code resent to:', result.number);
 * };
 * ```
 */
export const useResendPhoneVerificationCode = () => {
	const [resendMutation, { loading, error, data }] =
		useMutation<ResendPhoneVerificationCodeResponse>(
			ADD_AND_SEND_PHONE_NUMBER_VERIFICATION_CODE_MUTATION
		);

	/**
	 * Resend the SMS verification code
	 * @param phoneNumberId - The ID of the unverified phone number (from addPhoneNumbers response)
	 * @returns The phone number object on success, or throws on failure
	 */
	const resendCode = async ({ phoneNumberId }: { phoneNumberId: number }) => {
		try {
			const result = await resendMutation({
				variables: { phoneNumberId },
			});

			if (result.error) {
				const apolloError = result.error as any;
				const message = apolloError.graphQLErrors?.[0]?.message ?? apolloError.message;
				console.error('[useResendPhoneVerificationCode] GraphQL error:', message);
				throw new Error(message);
			}

			const phoneNumber = result.data?.addAndSendPhoneNumberVerificationCode?.phoneNumber ?? null;
			console.log('[useResendPhoneVerificationCode] Code resent to:', phoneNumber?.number);
			return phoneNumber;
		} catch (err) {
			throw err;
		}
	};

	return { resendCode, loading, error, data };
};
